import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Modal,
  IconButton,
  ToggleButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useApplyorganizationFilterMutation } from "../../../redux/Organization/Dashboard/DashboardfilterSlice";
import { useGetCustomersQuery } from "../../../redux/Organization/Dashboard/DashboardSlice";
import FilterBar from "@/Views/ExportJob/CreateNewCustomer/FilterBox";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import {
  LuRefreshCw,
  LuSettings,
  CgMoreO,
  IoMdSettings,
} from "@/Components/Icons/Icons";
import FormGenerator from "@/Utils/FormGenerator/FormGenerator";
import CustomizeList from "@/Views/ExportJob/Dashboard/CustomizeList";
import GridExample from "@/Components/Tables/AgGridTable";
import schema from "@/Config/Export/Dashboard/Dashboard";
import initialColumns from "@/Config/Export/CreateNewCustomer/TableColumn";
import { useUser } from "@/Context/UserContext";
import { useGlobalFilter } from "@/Context/GlobalFilterContext";

function Dashboard() {
  const navigate = useNavigate();
  const gridRef = useRef();

  // ✅ Table columns state
  const [openCustomize, setOpenCustomize] = useState(false);
  const [columnDefs, setColumnDefs] = useState(initialColumns);
  const visibleColumns = columnDefs.filter((col) => col.checked);
  const { clearAll, isDataFetched } = useUser();
  const [currentPage, setCurrentPage] = useState(1); // API 1-based
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [newloading, setNewLoading] = useState(false);
  const { financialYear, city } = useGlobalFilter();

  const { data, isLoading, refetch, isError, isFetching } =
    useGetCustomersQuery({
      page: currentPage,
      pageSize: rowsPerPage,
    });
  const rows = data?.rows ?? [];
  console.log("rows", rows);
  const totalCount = data?.totalCount ?? 0;

  const tableData = isError || rows.length === 0 ? [] : rows;
  // const tableData = data?.rows ?? [];
  React.useEffect(() => {
    refetch();
  }, []);

  // ✅ Filter API
  const [applyDashboardFilter] = useApplyorganizationFilterMutation();
  const [filteredData, setFilteredData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const handleSearch = async (filterPayload) => {
    if (!filterPayload) {
      setFilteredData(null);
      setIsFilterApplied(false);
      return;
    }
    setLoading(true);

    try {
      const res = await applyDashboardFilter(filterPayload).unwrap();
      console.log("res from filter", res);
      setFilteredData(res?.data.data ?? []);
      setIsFilterApplied(true);
    } catch (err) {
      setFilteredData([]);
      setIsFilterApplied(true);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Reset filter
  const handleResetFilter = () => {
    setLoading(true);

    setFilteredData(null);
    setIsFilterApplied(false);
    setLoading(false);
  };

  const finalData = isFilterApplied
    ? Array.isArray(filteredData)
      ? filteredData
      : [] // ensure array
    : Array.isArray(tableData)
      ? tableData
      : [];
      console.log("finalData", finalData);

  // on mount or tab return
  // useEffect(() => {
  //   const saved = localStorage.getItem("savedColumns");
  //   if (saved) {
  //     const parsed = JSON.parse(saved);
  //     setColumnDefs(parsed);
  //     gridRef.current?.api?.setColumnDefs(parsed);
  //   }
  // }, []);

  const handleCreateNewEntry = () => {
    setNewLoading(true);
    clearAll();
    navigate("/organization/create-customer");
  };

  const handleRowClick = (rowData) => {
    if (rowData?.customerId) {
      localStorage.setItem("customerId", rowData.customerId);
    }
    navigate(`${"/organization/create-customer"}/${rowData.customerId}`, {
      state: { rowData },
    });
  };
  localStorage.removeItem("customerId");

  // ✅ Add this below your API call section
  const [formValues, setFormValues] = useState({
    totalJobCreations: 0,
    totalExports: 0,
    Tracking: 0,
    ActiveClients: 0,
    totalJobCreationsPercentage: 0,
    totalExportsPercentage: 0,
    trackingPercentage: 0,
    activeClientsPercentage: 0,
    totalJobCreationsArrow: "Up",
    totalExportsArrow: "Up",
    trackingArrow: "Up",
    activeClientsArrow: "Up",
  });

  // ✅ Whenever your API data changes, update these dynamically
  useEffect(() => {
    if (!data) return;

    setFormValues({
      totalJobCreations: data?.totalCount ?? 0,
      totalExports: data?.totalCount ?? 0,
      Tracking: data?.TrackingCount ?? 0,
      ActiveClients: data?.TrackingCount ?? 0,

      totalJobCreationsPercentage: data?.lastWeekPercentage ?? 0,
      totalExportsPercentage: data?.lastMonthPercentage ?? 0,
      // trackingPercentage: data?.thisWeekPercentage ?? 0,
      // activeClientsPercentage: data?.thisMonthPercentage ?? 0,

      totalJobCreationsArrow: data?.weekComparison || "Up",
      totalExportsArrow: data?.monthComparison || "Up",
      // trackingArrow: data?.monthComparison || "Up",
      // activeClientsArrow: data?.weekComparison || "Up",
    });
  }, [data]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "var(--v-gap-sm)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="heading">Organization Overview</Typography>
          <Typography variant="bodySmall">
            You can see all the Jobs here
          </Typography>
        </Box>

        <Box display="flex" gap={2}>
          <BlackButton
            variant="bodySmallStrong"
            label="Create New Entry"
            iconName={!newloading ? "LuPlus" : null}
            loading={newloading}
            loadingText="Create New Entry"
            onClick={handleCreateNewEntry}
            sx={{
              width: 180,
              color: "var(--gray-50)",
              backgroundColor: "var(--brand-600)",
              textTransform: "none",
              borderRadius: 2,
              "&:hover": { backgroundColor: "var(--brand-600)" },
            }}
          />
        </Box>
      </Box>

      <FormGenerator
        schema={schema}
        showLoader={false}
        initialData={{
          totalJobCreations: {
            value: data?.totalCount ?? 0,
            percentage: data?.lastWeekPercentage ?? 0,
            arrowDirection: data?.weekComparison ?? "Up",
          },
          totalExports: {
            value: data?.totalCount ?? 0,
            percentage: data?.lastMonthPercentage ?? 0,
            arrowDirection: data?.monthComparison ?? "Up",
          },
          Tracking: {
            value: data?.Tracking ?? 0,
            // percentage: data?.thisWeekPercentage ?? 0,
            // arrowDirection: data?.trackingComparison ?? "Up",
          },
          ActiveClients: {
            value: data?.Tracking ?? 0,
            // percentage: data?.thisMonthPercentage ?? 0,
            // arrowDirection: data?.activeClientomparison ?? "Up",
          },
        }}
      />

      <FilterBar
        onSearch={handleSearch}
        onReset={handleResetFilter}
        setFilteredData={setFilteredData}
        setIsFilterApplied={setIsFilterApplied}
      />

      {/* Table */}
      <Box
        sx={{ borderRadius: "10px", backgroundColor: "var(--gray-0)", mb: 2 }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
        >
          <Typography variant="subheading">Jobs</Typography>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton sx={{ color: "var(--gray-500)" }} onClick={refetch}>
              <LuRefreshCw />
            </IconButton>

            <IconButton
              onClick={() => setOpenCustomize((prev) => !prev)}
              sx={{
                color: openCustomize ? "var(--brand-600)" : "var(--gray-500)",
                backgroundColor: openCustomize
                  ? "var(--brand-300)"
                  : "transparent",
                borderRadius: "50%",
                transition: "all 0.3s ease",
              }}
            >
              {openCustomize ? <IoMdSettings /> : <LuSettings />}
            </IconButton>

            <IconButton sx={{ color: "var(--gray-500)" }}>
              <CgMoreO />
            </IconButton>
          </Box>
        </Box>

        <GridExample
          type="organization" // or "export"
          gridRef={gridRef}
          columns={visibleColumns}
          tableData={finalData}
          totalCount={totalCount}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          handleRowClick={handleRowClick}
          loading={loading || isLoading || isFetching}
          refetch={refetch} // ✅ Pass refetch here
        />
      </Box>

      {/* Customize Modal */}
      <Modal open={openCustomize} onClose={() => setOpenCustomize(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "var(--gray-0)",
            boxShadow: 24,
            borderRadius: 2,
          }}
        >
          <CustomizeList
            columns={isError ? [] : columnDefs}
            // onSave={handleCustomizeSave}
            onClose={() => setOpenCustomize(false)}
          />
        </Box>
      </Modal>
    </Box>
  );
}

export default Dashboard;
